import { Component, OnInit, Input, OnDestroy, ElementRef, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { camelCase } from 'lodash';
// Services
import { LoggerService, BroadcastService } from '@cv-invision/core';
import { ViewsService } from '../../../services/views.service';
import { SchemasService } from '../../../services/schemas.service';
import { VisibilityContext } from '../../../services/visibility-context';

@Component({
	selector: 'cv-review-fields',
	templateUrl: './review-fields.component.html',
	styleUrls: ['./review-fields.component.scss']
})
export class ReviewFieldsComponent implements OnInit, OnDestroy {
	@Input() options: any;
	@ViewChild('reviewField', { read: ElementRef, static: false }) reviewField: ElementRef;

	formControl: FormControl;
	field: any;
	value: any;
	historical = [];

	paths = {
		original: '',
		jsonPath: '',
		schema: '',
		path: ''
	};

	show = {
		value: true,
		label: true
	};

	visible = true;
	active = false;

	get module(): any {
		return true;
	}
	get form(): FormGroup {
		return this.viewsService.form;
	}
	get order(): any {
		return this.viewsService.order;
	}
	get document(): any {
		return this.viewsService.document;
	}
	get historicalValues(): any {
		return this.viewsService.historical;
	}
	get isEditing(): boolean {
		return this.viewsService.isEditing;
	}

	historicalChanged$: Subscription;
	ruleSelectedChanged$: Subscription;
	formChanged$: Subscription;

	constructor(
		private viewsService: ViewsService,
		protected schemaService: SchemasService,
		private broadcastService: BroadcastService,
		private console: LoggerService
	) {
		//
	}

	async ngOnInit(): Promise<void> {
		await this.schemaService.setPaths(this.paths, this.options);
		await this.setFormControl();
		await this.setField();
		await this.setValue();

		await this.setHistorical();
		await this.isVisible();

		this.historicalChanged$ = this.viewsService.historicalChanged.subscribe(async data => {
			await this.setHistorical();
		});

		this.ruleSelectedChanged$ = this.viewsService.selectedRuleChanged.subscribe(async rule => {
			await this.highlightField(rule);
		});

		this.formChanged$ = this.form.valueChanges.subscribe(async data => {
			await this.setValue();
			await this.isVisible();
		});
	}

	ngOnDestroy(): void {
		if (this.historicalChanged$) {
			this.historicalChanged$.unsubscribe();
		}
		if (this.formChanged$) {
			this.formChanged$.unsubscribe();
		}
	}

	/**
	 * Set the field different paths,
	 */
	async setField(): Promise<void> {
		const field = await this.schemaService.getMetadataByPath(this.paths.schema);
		this.field = Object.assign({}, field);

		if ('show' in this.options) {
			Object.assign(this.show, this.options.show);
		}

		if ('label' in this.options) {
			const newLabels = {
				UILabel: this.options.label,
				DisplayText: this.options.label
			};

			Object.assign(this.field, newLabels);
		}
	}

	/**
	 * Set the value of the field
	 */
	async setValue(): Promise<void> {
		this.value = '--';
		const isCvds = this.order.schema.indexOf('InspectionReport') > -1;

		this.value = this.formControl.value;
		if (isCvds && this.field && this.field.DataType === 'Enumerated') {
			const enumValue = await this.schemaService.getEnumeratedValue(this.paths.schema, this.formControl.value);
			this.value = enumValue;
		}

		// Legacy code
		// ==================== DO NOT REMOVE ================================
		// Need to verify with the 1004,1073,1075 & 2055 to confirm that this doesnt break.

		// const isDesktopModule = this.module;
		// let fieldValue;

		// if (!isCvds) {
		// 	fieldValue = this.schemaService.value(this.document, `$.${this.paths.jsonPath}`);
		// } else {
		// 	fieldValue = this.schemaService.value(this.document, `$.${this.paths.path}`);
		// }

		// if (fieldValue) {
		// 	let fieldPlaceholder = fieldValue;

		// 	if (!isDesktopModule) {
		// 		fieldPlaceholder = fieldValue.Value;

		// 		if (fieldValue.Historical.length > 0) {
		// 			this.historical = this.formatHistorical(fieldValue.Historical);
		// 		}
		// 	}
		// }
	}

	/**
	 * initialize formcontrol from the given path
	 */
	async setFormControl(): Promise<any> {
		let formData: any = this.form;

		this.paths.path.split('.').forEach(path => {
			const pathPattern = /(\[[0-9]+\])/g;
			const matches = path.match(pathPattern);
			let isArray = false;
			let pathIndex;

			if (matches) {
				isArray = true;
				// eslint-disable-next-line
				pathIndex = matches[0].replace(/([\[\]])/g, '');
				path = path.replace(pathPattern, '');
			}

			formData = formData.get(path);
			if (isArray) {
				formData = formData.at(pathIndex);
			}
		});

		this.formControl = formData;
	}

	/**
	 * Set the historical array values
	 */
	async setHistorical(): Promise<void> {
		const pattern = `$..[?(@.path=="${this.paths.original}")]`;
		this.historical = (this.schemaService.query(this.historicalValues, pattern) || []).map(jpChanges => {
			return {
				Val: jpChanges.previousValue
			};
		});

		// Legacy code, DO NOT REMOVE
		// if (!this.module) {
		// 	this.setHistoricalNonStandarized();
		// }
	}

	/**
	 * Set the historical array values for non standarizes return values
	 */
	async setHistoricalNonStandarized(fieldValue: any): Promise<void> {
		if (fieldValue.Historical.length > 0) {
			const formated = [];
			fieldValue.Historical.forEach(item => {
				const groupParse: any = [];
				Object.keys(item).forEach(key => {
					const nKey = camelCase(key);
					groupParse[nKey] = item[key];
				});
				formated.push(Object.assign({}, groupParse));
			});
			this.historical = formated;
		}
	}

	/**
	 * open historical component
	 * @param item
	 * @param historical
	 */
	async showHistorical(elem: ElementRef): Promise<void> {
		const {height, left, width, top} = await elem.nativeElement.getBoundingClientRect();
		const offsetHeight = height;
		const offsetLeft = left - (width / 2);
		const offsetTop = top;
		const offsetWidth = width;
		const scrollTop = 0;
		this.broadcastService.broadcast({
			name: 'historicalValueEvent',
			event: {
				isOpen: true,
				data: this.historical,
				pos: { offsetHeight, offsetLeft, offsetTop, offsetWidth, scrollTop }
			}
		});
	}

	/**
	 * Evaluate the conditionality to show/hide the field
	 */
	async isVisible(): Promise<void> {
		let visible = true;
		if (this.field.Conditionally !== undefined) {
			// tslint:disable-next-line:no-eval
			const evaluation: any = await eval(`(${this.field.Conditionally})`)({}, new VisibilityContext(this.formControl, this.schemaService));
			visible = evaluation as boolean;

			if (!visible) {
				this.formControl.setValue('', { onlySelf: true, emitEvent: false });
			}
		}

		this.visible = visible;
	}

	/**
	 * Mark the field as active and scroll to the element
	 * @param rule
	 */
	async highlightField(rule: any): Promise<void> {
		this.active = rule.indexOf(this.paths.original) > -1;
		if (this.active) {
			let scrollOptions: any = false;
			if (window.innerHeight > 750) {
				scrollOptions = {
					behavior: 'smooth',
					block: 'start'
				};
			}
			this.reviewField.nativeElement.scrollIntoView(scrollOptions);
		}
	}
}
